import { Checkbox } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { PersonApi, PersonGridDto, PersonSearch } from "../../../../api";
import {
  AddButton,
  EditButton,
  EmailButton,
  FusionButton,
  SearchTablePage,
  SplitButton,
  ViewButton,
} from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { generateAddress } from "../../../../utils";

export interface IPersonListPageProps {}

export const PersonListPage: React.FunctionComponent<IPersonListPageProps> = (props) => {
  const api = useApiService(PersonApi);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const selectionList = React.useMemo(() => [], []);

  const fuse = React.useCallback(() => {
    if (selectionList.length !== 2) {
      showError(t(ETLCodes.FuseSelectionError));
    } else {
      window.localStorage.setItem("personFusionId", JSON.stringify(selectionList[1].personId));
      history.push(`${ERoutes.personFusion}/${selectionList[0].personId}/fusion/edit`);
    }
  }, [history, selectionList, t]);

  const split = React.useCallback(() => {
    if (selectionList.length !== 1 || selectionList.includes((e) => e.parentPersonId != null)) {
      showError(t(ETLCodes.SplitSelectionError));
    } else {
      history.push(`${ERoutes.personSplit}/${selectionList[0].personId}/split/edit`);
    }
  }, [history, selectionList, t]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: PersonGridDto) => (
          <ButtonContainer>
            <ViewButton minimal={true} onClick={() => history.push(`${ERoutes.people}/${row.personId}/detail/view`)} />
            <EditButton minimal={true} onClick={() => history.push(`${ERoutes.people}/${row.personId}/detail/edit`)} />
            <Checkbox
              checked={selectionList.find((c) => c === row)}
              onChange={() => {
                if (selectionList.includes(row)) {
                  const index = selectionList.findIndex((e) => e === row);
                  selectionList.splice(index, 1);
                } else {
                  selectionList.push(row);
                }
              }}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.IsAMember),
        fieldName: "isMember",
        autoFitContent: true,
        alignment: "center",
        computed: true,
        disableSorting: true,
        render: (row: PersonGridDto) => <Checkbox disabled checked={row.isMember} />,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "isBreeder",
        autoFitContent: true,
        render: (row: PersonGridDto) => {
          if (row.isBreeder) {
            return <i className="fas fa-paw" />;
          }
        },
      },
      {
        header: () => t(ETLCodes.LastName),
        fieldName: "lastName",
      },
      {
        header: () => t(ETLCodes.FirstName),
        fieldName: "firstName",
      },
      {
        header: () => t(ETLCodes.MailAddress),
        fieldName: "address",
        disableSorting: true,
        computed: true,
        render: (row: PersonGridDto) => generateAddress(row.number, row.street, row.city, row.postalCode, row.country),
      },
      {
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (row: PersonGridDto) => <EmailButton email={row.email} text={row.email} />,
      },
      {
        header: () => t(ETLCodes.PhoneNumber),
        fieldName: "phoneNumber",
      },
      {
        header: () => t(ETLCodes.MobilePhoneNumber),
        fieldName: "mobilePhoneNumber",
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.WebAccess),
        headerAlignment: "center",
        fieldName: "webAccess",
        alignment: "center",
        render: (row: PersonGridDto) => <Checkbox disabled checked={row.webAccess} />,
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.IsKnownPerson),
        headerAlignment: "center",
        fieldName: "isKnownPerson",
        alignment: "center",
        render: (row: PersonGridDto) => <Checkbox disabled checked={row.isKnownPerson} />,
      },
    ],
    [history, selectionList, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.people}/0/detail/edit`);
  }, [history]);

  const search = React.useCallback(
    (sObj) => api.baseSearch(PersonSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId })),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      initialSearch={true}
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(PersonSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId }))
      }
      rightElement={
        <div>
          <AddButton onClick={addItemFunction} text={t(ETLCodes.General_Add)} />
          <FusionButton onClick={fuse} text={t(ETLCodes.General_Group)} />
          <SplitButton onClick={split} text={t(ETLCodes.General_Split)} />
        </div>
      }
      sortKeys={{ lastName: "ASC" }}
      breadCrumbs={[{ text: t(ETLCodes.People) }]}
      filterMode="OnEnter"
    />
  );
};
